import { Download, FileBadge } from "lucide-react"

import { KeyValue } from "@/components/KeyValue"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { formatSignedAtDateTime } from "@/lib/documents"

/**
 * document-editor.md's wrapping override for these rows. `KeyValue`'s own
 * default is a single-line `inline-flex`; these labels run to «Підрозділ
 * організації, до якого належить власник сертифіката:», so the row has to
 * wrap and align the value to the label's last baseline.
 */
const kvRow = "flex flex-wrap items-baseline gap-x-2 gap-y-1"

/**
 * The signer's certificate attributes, as listed on the «ЕЦП» tab of the
 * previous ARM (настанови СГ.002.10.КО, п. 3.5.5, рис. 3.65) and carried
 * into document-editor.md's `SignatureDetailsDialog`.
 *
 * Every field is optional because **no current API response carries any of
 * them**: `submitSignedOtkDocument` returns the updated document
 * (`status`/`version`/`signedAt`) only, and `KepKeyReader` surfaces nothing
 * but a display label for the carrier. The wireframe fills these from its
 * `connectedKepKeys` mock; the spec itself flags the values as demo data and
 * the real field set as an open question for the customer (OPEN-QUESTIONS.md
 * п. 49, «як зберігаються дані підпису»). So this type is the seam to wire
 * once that is answered — values the app cannot source render as «—» rather
 * than being invented here.
 */
export interface SignatureCertificateDetails {
  /** «Ім'я ЦСК, що видав сертифікат:» */
  issuerName?: string
  /** «Реквізити ЦСК, що видав сертифікат:» */
  issuerIdentifier?: string
  /** «Реквізити власника сертифіката:» */
  ownerIdentifier?: string
  /** «Ім'я власника сертифіката:» */
  ownerName?: string
  /** «Реєстраційний номер сертифіката:» */
  certificateSerial?: string
  /** «Організація, до якої належить власник сертифіката:» */
  organisation?: string
  /** «Підрозділ організації, до якого належить власник сертифіката:» */
  organisationUnit?: string
  /** «Повне ім'я власника сертифіката:» */
  ownerFullName?: string
  /** «Посада власника сертифіката:» */
  ownerPosition?: string
  /** «Назва держави, до якої належить власник сертифіката:» */
  country?: string
  /** «Назва населеного пункту, до якого належить власник сертифіката:» */
  locality?: string
  /** «Адреса власника сертифіката:» */
  address?: string
  /** «Довжина відкритого ключа в бітах:» */
  publicKeyLength?: string
  /** «Ідентифікатор відкритого ключа сертифіката:» */
  publicKeyIdentifier?: string
  /** «Час введення сертифіката в дію:» */
  validFrom?: string
  /** «Дата закінчення дії сертифіката:» */
  validUntil?: string
}

/**
 * document-editor.md's «Деталі Підпису» — the read-only certificate/signature
 * card behind the "Документ Підписано" block's own full-width button. Opens
 * and closes on its own state; nothing here mutates the document.
 */
export function SignatureDetailsDialog({
  signedAt,
  details,
}: {
  /** The KEP instant this signature carries, as the sign flow reported it. */
  signedAt: Date | null
  details?: SignatureCertificateDetails
}) {
  const dash = "—"
  const rows: [string, string][] = [
    ["Статус:", "КЕП накладено"],
    [
      "Час підпису або мітка часу:",
      signedAt ? formatSignedAtDateTime(signedAt.toISOString()) : dash,
    ],
    ["Ім'я ЦСК, що видав сертифікат:", details?.issuerName ?? dash],
    ["Реквізити ЦСК, що видав сертифікат:", details?.issuerIdentifier ?? dash],
    ["Реквізити власника сертифіката:", details?.ownerIdentifier ?? dash],
    ["Ім'я власника сертифіката:", details?.ownerName ?? dash],
    ["Реєстраційний номер сертифіката:", details?.certificateSerial ?? dash],
    [
      "Організація, до якої належить власник сертифіката:",
      details?.organisation ?? dash,
    ],
    [
      "Підрозділ організації, до якого належить власник сертифіката:",
      details?.organisationUnit ?? dash,
    ],
    ["Повне ім'я власника сертифіката:", details?.ownerFullName ?? dash],
    ["Посада власника сертифіката:", details?.ownerPosition ?? dash],
    [
      "Назва держави, до якої належить власник сертифіката:",
      details?.country ?? dash,
    ],
    [
      "Назва населеного пункту, до якого належить власник сертифіката:",
      details?.locality ?? dash,
    ],
    ["Адреса власника сертифіката:", details?.address ?? dash],
    ["Довжина відкритого ключа в бітах:", details?.publicKeyLength ?? dash],
    [
      "Ідентифікатор відкритого ключа сертифіката:",
      details?.publicKeyIdentifier ?? dash,
    ],
    ["Час введення сертифіката в дію:", details?.validFrom ?? dash],
    ["Дата закінчення дії сертифіката:", details?.validUntil ?? dash],
  ]

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="w-full" variant="outline">
          <FileBadge data-icon="inline-start" />
          Деталі Підпису
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Деталі Підпису</DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[70vh] gap-2 overflow-y-auto">
          {rows.map(([label, value]) => (
            <KeyValue
              className={kvRow}
              key={label}
              label={label}
              value={value}
            />
          ))}
        </div>
        <DialogFooter>
          {/*
            The spec's footer action is an `<a target="_blank">` to the same
            placeholder PDF the wireframe's «Скачати Електронний Протокол»
            uses. This package has no such stub and no endpoint that renders
            signature data as PDF, so the documented affordance is rendered
            disabled rather than pointed at a URL that does not exist —
            the same call `ConsentDownload` makes for its own deferred
            print/PDF generation.
          */}
          <Button
            className="text-primary hover:text-primary sm:mr-auto"
            disabled
            variant="ghost"
          >
            <Download data-icon="inline-start" />
            Зберегти в файлі PDF
          </Button>
          <DialogClose asChild>
            <Button>Закрити</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
